import { useId, useState } from 'react';
import { CheckIcon, CircleAlertIcon, MinusIcon } from 'lucide-react';
import { WorkspaceIcon, type WorkspaceIconName } from '@/workspace/icons';
import { ActionButton } from './action-button';
import { useActionSummary, statusLabel } from './action-approval-copy';
import { ActionRowMore } from './action-row-more';
import { compactActionLabel, artifactActionLabel } from './action-labels';
import { ActionExpiry, useActionExpiry } from './action-expiry';
import { AskAgain } from './action-ask-again';
import { useNamespaceCopy } from '@/i18n/ns';
import buildCopy from '@/i18n/ns/build-experience';
import { ActionReceipt } from './action-receipt';
import { actionResultTarget } from './action-result-target';
import { actionUsesCanvas } from './action-layout';
import { isStoreReadTool } from '@/workspace/storefront/read-request';
import { useScheduleCardRuns } from './schedule-preview';
import type { ActionView } from './action-confirmation';
import type { useActionConfirmation } from './use-action-confirmation';

type State = ReturnType<typeof useActionConfirmation>;
export function ActionInline({ state, action }: { state: State; action: ActionView }) {
  const {
    workspace,
    actionText,
    surfaceCopy,
    copy,
    locale,
    busy,
    canApprove,
    uncertain,
    mutate,
    notice,
    checkAgain,
  } = state;
  const expiry = useActionExpiry(action.expiresAt);
  const expiryId = useId();
  const summary = useActionSummary()(action);
  // A schedule card that will run names its next runs, from the server, beside what the tap approves.
  const upcoming = useScheduleCardRuns(action);
  // Which decision is in flight, so the spinner sits on the control that was used.
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const build = useNamespaceCopy(buildCopy);
  // Historical requested builds stay receipts. No picker, polling, new build or Finish UI.
  if (state.requestedBuild)
    return (
      <section className="pc-action-status-line" data-status={action.status} role="status">
        <span className="pc-action-status-label">
          {action.status === 'pending' || action.status === 'running'
            ? actionText.running
            : action.status === 'executed'
              ? build.buildStartedChip
              : statusLabel(action.status, actionText)}
        </span>
      </section>
    );
  if (actionUsesCanvas(action)) {
    const target = actionResultTarget(action) ?? { kind: 'action' as const, id: action.id };
    const current = workspace?.canvas?.kind === target.kind && workspace.canvas.id === target.id;
    // A paid Storefront read has no name of its own: the card names what it reads.
    const name =
      action.args.name ?? action.args.title ?? (isStoreReadTool(action.tool) ? summary : null);
    // One state for the card on every width: a pending request past its expiry is expired
    // here exactly as it is in the canvas review.
    const shown = expiry.expired && action.status === 'pending' ? 'expired' : action.status;
    const effective = shown === action.status ? action : { ...action, status: shown };
    return (
      <>
        <section className="pc-action-status-line" data-status={action.status} aria-label={summary}>
          <button
            type="button"
            className="pc-action-status-link"
            data-canvas-trigger
            data-state={shown}
            aria-label={`${actionText.open}: ${summary}`}
            aria-current={current ? 'true' : undefined}
            onClick={() => workspace?.openCanvas(target)}
            disabled={!workspace}
          >
            <ArtifactGlyph tool={action.tool} state={shown} />
            <span className="pc-action-status-summary">
              <span className="pc-action-status-label">
                {/* A decided read says what happened to it, never "Approval needed". */}
                {isStoreReadTool(action.tool) && shown !== 'pending'
                  ? shown === 'expired'
                    ? surfaceCopy.approvalExpired
                    : statusLabel(shown, actionText)
                  : artifactActionLabel(effective, surfaceCopy, locale)}
              </span>
              {typeof name === 'string' && <span className="pc-action-status-name">{name}</span>}
            </span>
            <span className="pc-action-status" role="status">
              {/* An expired card's leading label already says so: no second "Expired" chip. */}
              {shown !== 'expired' && (
                <span className="pc-action-status-text">
                  {shown === 'pending'
                    ? actionText.review
                    : shown === 'executed' || isStoreReadTool(action.tool)
                      ? actionText.open
                      : statusLabel(shown, actionText)}
                </span>
              )}
              <WorkspaceIcon name="chevron" size={14} />
            </span>
          </button>
          {shown === 'expired' && (
            <p className="pc-action-status-detail">
              {surfaceCopy.expiredDetail}{' '}
              {!workspace?.readOnly && <AskAgain origin={state.origin} copy={surfaceCopy} />}
            </p>
          )}
        </section>
      </>
    );
  }
  if (action.status !== 'pending' || expiry.expired)
    return (
      <ActionReceipt
        action={
          expiry.expired && action.status === 'pending' ? { ...action, status: 'expired' } : action
        }
        state={state}
        fresh={decision !== null}
      />
    );
  const sentence = compactActionLabel(action, surfaceCopy, actionText, locale, false, summary);
  const label = upcoming ? `${sentence} ${upcoming}` : sentence;
  return (
    <section
      className="pc-action-inline"
      data-status={action.status}
      aria-label={label}
      aria-describedby={expiryId}
      title={surfaceCopy.expiresAt(
        new Date(action.expiresAt).toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
        }),
      )}
    >
      <div className="pc-action-inline-row">
        {/* The glyph travels with the title, so a label that wraps never leaves it alone on a line. */}
        <span className="pc-action-inline-title">
          <WorkspaceIcon name={rowGlyph(action)} size={14} className="pc-action-inline-glyph" />
          <span>{label}</span>
        </span>
        <ActionExpiry
          id={expiryId}
          expiresAt={action.expiresAt}
          copy={surfaceCopy}
          locale={locale}
          near={expiry.near}
        />
        {uncertain ? (
          <>
            <span role="status">{surfaceCopy.uncertain}</span>
            <ActionButton
              variant="secondary"
              busy={busy}
              disabled={busy}
              onClick={() => void checkAgain()}
            >
              {surfaceCopy.checkAgain}
            </ActionButton>
          </>
        ) : action.canDecide ? (
          // A binary consent shows both answers (ACTIONS-DESIGN §6.2, §9.1): Reject
          // (quiet) then Approve (primary). Only Details waits behind ⋯.
          <div className="pc-action-confirmation-actions">
            <ActionButton
              variant="quiet"
              busy={busy && decision === 'reject'}
              disabled={busy}
              onClick={() => {
                setDecision('reject');
                void mutate('reject');
              }}
            >
              {actionText.reject}
            </ActionButton>
            <ActionButton
              variant="primary"
              busy={busy && decision !== 'reject'}
              disabled={busy || !canApprove}
              onClick={(event) => {
                setDecision('approve');
                void mutate('approve', undefined, event);
              }}
            >
              {actionText.approve}
            </ActionButton>
            <ActionRowMore label={copy.more} state={state} action={action} />
          </div>
        ) : (
          <span role="status">{surfaceCopy.waitingFor(action.accountLabel ?? copy.account)}</span>
        )}
      </div>
      {(notice || action.error) && (
        <p className="pc-action-confirmation-error" role="alert">
          {notice || action.error}
        </p>
      )}
    </section>
  );
}

const ARTIFACT_GLYPHS: Record<string, WorkspaceIconName> = {
  create_skill_draft: 'skills',
  update_skill_draft: 'skills',
  create_agent: 'agents',
  save_work_result: 'results',
  propose_company_knowledge: 'knowledge',
  propose_knowledge_change: 'knowledge',
  suggest_page_edits: 'knowledge',
};

/** Presentation only: the kind icon while pending, the outcome once decided. */
function ArtifactGlyph({ tool, state }: { tool: string; state: ActionView['status'] }) {
  if (state === 'executed') return <CheckIcon aria-hidden="true" />;
  if (state === 'failed') return <CircleAlertIcon aria-hidden="true" />;
  if (['rejected', 'expired', 'superseded'].includes(state))
    return <MinusIcon aria-hidden="true" />;
  return <WorkspaceIcon name={ARTIFACT_GLYPHS[tool] ?? 'file'} size={16} />;
}

/** The row's leading glyph follows the exact tool and requested value, never prose. */
function rowGlyph(action: ActionView): WorkspaceIconName {
  if (action.tool === 'save_memory' || action.tool === 'forget_memory') return 'person';
  if (action.tool === 'approve_app_outside_access') return 'globe';
  if (action.tool === 'set_app_event') return action.args.kind === 'webhook' ? 'globe' : 'table';
  if (action.tool === 'set_app_schedule') return 'clock';
  if (action.tool === 'set_app_data_writes') return 'database';
  if (action.tool === 'apply_app_table_change') return 'table';
  if (action.tool === 'set_app_connected_write') return 'connections';
  if (action.tool === 'confirm_app_code') return 'shield';
  if (action.tool === 'run_app_function_with_changes') return 'play';
  if (action.tool !== 'update_my_settings') return 'settings';
  if (action.args.theme === 'dark') return 'moon';
  if (action.args.theme === 'light') return 'sun';
  if (typeof action.args.language === 'string') return 'globe';
  if (typeof action.args.timezone === 'string') return 'clock';
  return 'settings';
}
